import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import io from 'socket.io-client';
import ChatService from '../../services/chat.service';
import authHeader from '../../services/auth-header';
import { API_URL, CHAT_API_URL } from '../../services/config';
import { useAuth } from '../../contexts/AuthContext';
import './ChatWidget.css';

function useSocket(token){
  const socket = useMemo(()=> {
    const sock = io(API_URL.replace(/\/api\/?$/,''), { autoConnect:false, auth:{ token } });
    
    sock.on('connect', () => {
      console.log(`[${new Date().toISOString()}] Socket connected:`, sock.id);
    });
    
    sock.on('disconnect', () => {
      console.log(`[${new Date().toISOString()}] Socket disconnected`);
    });
    
    sock.on('connect_error', (error) => {
      console.error(`[${new Date().toISOString()}] Socket connection error:`, error);
    });
    
    return sock;
  }, [token]);
  
  useEffect(()=>{ 
    if(token){ 
      console.log(`[${new Date().toISOString()}] Connecting socket with token`);
      socket.connect(); 
      return ()=> socket.disconnect(); 
    } 
  }, [socket, token]);
  return socket;
}

export default function ChatWidget(){
  const { user, isLoggedIn, isOnDashboard } = useAuth();
  const token = authHeader().Authorization?.replace('Bearer ','');
  const socket = useSocket(token);
  const [open,setOpen]=useState(false);
  const [conversation,setConversation]=useState(null);
  const [messages,setMessages]=useState([]);
  const [text,setText]=useState('');
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const fileRef = useRef();
  const listRef = useRef();

  useEffect(()=>{
    if(!open) return;
    setLoading(true);
    (async()=>{
      try {
        const convo = await ChatService.openConversation();
        setConversation(convo);
        const msgs = await ChatService.getMessages(convo.id);
        setMessages(msgs);
        // Mark messages as read and reset unread count
        await ChatService.markRead(convo.id);
        setUnreadCount(0);
        setTimeout(()=> listRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }), 50);
      } catch (error) {
        console.error('Error loading conversation:', error);
      } finally {
        setLoading(false);
      }
    })();
  },[open]);

  const checkUnreadMessages = useCallback(async () => {
    try {
      const convos = await ChatService.listConversations();
      if (convos && convos.length > 0) {
        const convo = convos[0]; // Get the user's conversation
        const msgs = await ChatService.getMessages(convo.id);
        // Count unread messages (messages not from the current user)
        const currentUserId = user?.id;
        const unreadCount = msgs.filter(msg => 
          msg.senderId !== currentUserId && !msg.isRead
        ).length;
        setUnreadCount(unreadCount);
      }
    } catch (error) {
      console.error('Error checking unread messages:', error);
    }
  }, [user?.id]);

  // Check for unread messages on component mount and periodically
  useEffect(() => {
    checkUnreadMessages();
    
    // Check for unread messages every 30 seconds
    const interval = setInterval(checkUnreadMessages, 30000);
    
    return () => clearInterval(interval);
  }, [checkUnreadMessages]);

  // Refresh messages every 5 seconds when chat is open
  useEffect(() => {
    if (!open || !conversation) return;
    
    const refreshInterval = setInterval(async () => {
      try {
        const msgs = await ChatService.getMessages(conversation.id);
        setMessages(msgs);
      } catch (error) {
        console.error('Error refreshing messages:', error);
      }
    }, 5000);
    
    return () => clearInterval(refreshInterval);
  }, [open, conversation]);

  useEffect(()=>{
    if(!socket || !conversation) return;
    
    const onNew = (msg)=>{
      if(msg.conversationId !== conversation.id) return;
      
      setMessages(prev => {
        // Check if message already exists to avoid duplicates
        const exists = prev.some(m => m.id === msg.id);
        if (exists) return prev;
        
        // If this is a message from the current user, replace any temporary message with same content
        const currentUserId = user?.id;
        if (msg.senderId === currentUserId) {
          // Remove temporary message with same content and add the real one
          const filtered = prev.filter(m => !(m.isTemp && m.content === msg.content && m.senderId === currentUserId));
          return [...filtered, msg];
        }
        
        return [...prev, msg];
      });
      
      // Only increment unread count if chat is closed and message is not from current user
      const currentUserId = user?.id;
      if (!open && msg.senderId !== currentUserId) {
        setUnreadCount(prev => prev + 1);
      }
      
      setTimeout(()=> listRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }), 50);
    };
    
    const onRead = (data) => {
      // Handle read receipts if needed
    };
    
    socket.on('message:new', onNew);
    socket.on('message:read', onRead);
    
    return ()=>{
      socket.off('message:new', onNew);
      socket.off('message:read', onRead);
    };
  },[socket, conversation, open, user?.id]);

  const send = async ()=>{
    if(!text.trim() || !conversation) return;
    const messageText = text.trim();
    setText(''); // Clear input immediately for better UX
    
    // Create a temporary message to show immediately
    const tempMessage = {
      id: `temp-${Date.now()}`, // Temporary ID
      conversationId: conversation.id,
      senderId: user?.id,
      type: 'text',
      content: messageText,
      createdAt: new Date().toISOString(),
      isRead: true,
      isTemp: true // Flag to identify temporary messages
    };
    
    // Add temporary message immediately for instant feedback
    setMessages(prev => [...prev, tempMessage]);
    setTimeout(()=> listRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }), 50);
    
    // Set a timeout to remove temporary message if it doesn't get replaced
    const tempTimeout = setTimeout(() => {
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
    }, 5000); // 5 second timeout - more aggressive
    
    try {
      await ChatService.sendMessage(conversation.id, messageText);
      // Clear the timeout since message was sent successfully
      clearTimeout(tempTimeout);
    } catch (error) {
      console.error('Error sending message:', error);
      setText(messageText); // Restore text if sending failed
      // Remove the temporary message if sending failed
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
      clearTimeout(tempTimeout);
    }
  };

  const upload = async (e)=>{
    const f = e.target.files?.[0];
    if(!f || !conversation) return;
    
    try {
      await ChatService.upload(conversation.id, f);
      // Don't add to messages here - let socket event handle it
      fileRef.current.value='';
      setTimeout(()=> listRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }), 50);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  // Don't show chat widget if user is not logged in or not on dashboard
  if (!isLoggedIn || !isOnDashboard) {
    return null;
  }

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <>
      <div className="chat-fab" onClick={()=> setOpen(v=>!v)} title={open ? "Close chat" : "Chat with support"}>
        {open ? "✕" : "💬"}
        {unreadCount > 0 && !open && (
          <span className="unread-badge">{unreadCount}</span>
        )}
      </div>
      {open && (
        <div className="chat-panel">
          <div className="chat-header">
            <span>Support Assistant</span>
            <span onClick={()=> setOpen(false)}>✕</span>
          </div>
          
          {messages.length === 0 && (
            <div className="chat-started">
              Chat started at {formatTime(new Date())}
            </div>
          )}
          
          <div className="chat-messages" ref={listRef}>
            {loading ? (
              <div className="chat-loading">Loading conversation...</div>
            ) : messages.length === 0 ? (
              <div className="chat-empty">
                <div className="chat-empty-icon">🤖</div>
                <div className="chat-empty-text">
                  <div>Hi there! 👋</div>
                  <div>I'm your Support Assistant.</div>
                  <div>How can I help you today?</div>
                </div>
              </div>
            ) : (
              messages.map(m => (
                <div key={m.id} className={`msg ${m.senderId===conversation?.clientId ? 'me':'them'} ${m.isTemp ? 'temp' : ''}`}>
                  <div className="msg-avatar">
                    {m.senderId === conversation?.clientId ? 
                      getInitials(user?.firstName + ' ' + user?.lastName) : 
                      'SA'
                    }
                  </div>
                  <div className="msg-content">
                    <div className="msg-sender">
                      {m.senderId === conversation?.clientId ? 
                        `${user?.firstName} ${user?.lastName}` : 
                        'Support Assistant'
                      }
                    </div>
                    <div className="msg-bubble">
                      {m.type==='text' ? (
                        m.content
                      ) : (
                        <div className="msg-file">
                          {m.mimeType?.startsWith('image/') ? (
                            <img 
                              src={`${CHAT_API_URL}${m.fileUrl.replace('/api/chat/','')}`} 
                              alt={m.fileName} 
                            />
                          ) : (
                            <a 
                              href={`${CHAT_API_URL}${m.fileUrl.replace('/api/chat/','')}`} 
                              target="_blank" 
                              rel="noreferrer"
                            >
                              📎 {m.fileName || 'Download file'}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="msg-time">
                      {formatTime(m.createdAt)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="chat-composer">
            <button 
              className="attachment-btn" 
              onClick={()=> fileRef.current.click()}
              title="Attach file"
            >
              📎
            </button>
            <input ref={fileRef} type="file" onChange={upload} accept="image/*,application/pdf"/>
            <textarea 
              value={text} 
              onChange={e=> setText(e.target.value)} 
              placeholder="Type your message..." 
              onKeyDown={(e)=>{ 
                if(e.key==='Enter' && !e.shiftKey){ 
                  e.preventDefault(); 
                  send(); 
                } 
              }} 
            />
            <button onClick={send} title="Send message">
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}